<script lang="ts">
import { IconButton } from '@goodlifeplus/component-library'
import { ThemeType } from '@goodlifeplus/themes'
import { defineComponent } from 'vue'

/**
 * Back chevron + divider shown next to the logo on flows that opt in (e.g.
 * checkout). Encapsulates the design-system `IconButton` and its "bare chevron"
 * treatment so the header itself carries no button-specific styling. Colour
 * follows the header via `currentColor`, set by a theme-aware text class.
 */
export default defineComponent({
  components: { IconButton },
  computed: {
    isDarkHeader(): boolean {
      return this.$theme.type !== ThemeType.Default
    },
  },
  methods: {
    goBack(): void {
      // Prefer in-app history; fall back home for a deep-linked entry.
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        void this.$router.push({ name: 'home' })
      }
    },
  },
})
</script>

<template>
  <div
    class="flex items-center"
    :class="isDarkHeader ? 'text-white' : 'text-dark-navy'"
  >
    <IconButton
      class="app-header-back -ml-3 flex-shrink-0"
      data-cy="header-back"
      label="Go back"
      :variant="UI.IconButton.Variant.Tertiary"
      :size="UI.IconButton.Size.Large"
      :icon="UI.Icon.Name.ChevronLeft"
      @click="goBack"
    />
    <div
      class="mr-3 h-6 w-px flex-shrink-0"
      :class="isDarkHeader ? 'bg-white/30' : 'bg-slate-200'"
    />
  </div>
</template>

<style lang="scss" scoped>
// The DS IconButton variants all carry a filled background; strip it so the
// button reads as a bare chevron, and follow the surrounding text colour.
.app-header-back {
  --glp-button-tertiary-default-background: transparent;
  --glp-button-tertiary-hover-background: transparent;
  --glp-button-tertiary-pressed-background: transparent;
  --glp-button-tertiary-default-color-border: transparent;
  --glp-button-tertiary-hover-color-border: transparent;
  --glp-button-tertiary-pressed-color-border: transparent;
  --glp-button-tertiary-default-foreground: currentColor;
  --glp-button-tertiary-hover-foreground: currentColor;
  --glp-button-tertiary-pressed-foreground: currentColor;
}
</style>
